import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api } from './api.js';
import { useAuth } from './auth.jsx';

// Single shared subscription to /api/license/status. The Settings widget
// uses it for the status pill, the LicenseBanner uses it for the global
// strip across the top. Polling cadence matches the server-side poller
// (60s) — values change on a human timescale so faster polling buys
// nothing. visibilitychange forces a refresh when the user comes back
// to the tab so the banner is never more than a few seconds stale.
//
// Provider only polls when there's a logged-in user — otherwise we'd
// trigger 401s on the login page that would bounce the user back to
// /login in a loop via the existing auth:unauthorized event.

const LicenseContext = createContext(null);

const POLL_INTERVAL_MS = 60_000;

// Mirror of services/license.js RESTRICTIVE_STATUSES — keep in sync if
// the server-side list changes. Used by the banner to decide tone, by
// any client-side guard that wants to mirror the server gate visually.
export const RESTRICTIVE_STATUSES = new Set([
  'unknown',
  'ipc_unreachable',
  'not_activated',
  'expired',
  'revoked',
  'hardware_mismatch',
  'software_uuid_mismatch',
  'signature_invalid',
  'tampering_detected',
  'unsupported_token_version',
  'unsupported_crypto_suite'
]);

export const WARNING_STATUSES = new Set([
  'active_with_warning',
  'time_unsynced'
]);

export function LicenseProvider({ children }) {
  const { user } = useAuth();
  const [snapshot, setSnapshot] = useState(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  useEffect(() => {
    if (!user) {
      setSnapshot(null);
      return;
    }
    let cancelled = false;
    async function pull() {
      if (cancelled) return;
      try {
        const r = await api.get('/license/status');
        if (!cancelled) setSnapshot(r);
      } catch {
        // Network/auth errors are not fatal — keep the previous snapshot.
        // 401 is handled globally by auth.jsx's unauthorized listener.
      }
    }
    pull();
    const t = setInterval(pull, POLL_INTERVAL_MS);
    const onVis = () => { if (document.visibilityState === 'visible') pull(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVis);
    };
    // refreshNonce in deps lets external code force a poll (e.g. after
    // the user clicks "Renew license" in Settings — the banner should
    // disappear without waiting for the next tick).
  }, [user, refreshNonce]);

  return (
    <LicenseContext.Provider value={{ snapshot, refresh }}>
      {children}
    </LicenseContext.Provider>
  );
}

export function useLicense() {
  return useContext(LicenseContext) ?? { snapshot: null, refresh: () => {} };
}

// Convenience: true when the licence snapshot is in any restrictive
// status (or hasn't loaded yet — fail-closed). Used by the SPA to
// flip admin → user UI guards without an extra /auth/me round-trip
// when the licence flips mid-session.
export function useLicenseIsRestrictive() {
  const { snapshot } = useLicense();
  return !snapshot || RESTRICTIVE_STATUSES.has(snapshot.status);
}

// Effective role for THIS user under the current licence. Mirrors the
// server-side computeEffectiveRole in middleware/auth.js: an admin
// under a restrictive licence is demoted to 'user' for the duration
// of the restriction. The server is still the source of truth for
// what writes succeed — this hook just lets the UI hide/disable
// admin-only controls instead of letting the user click and get a 403.
//
// Falls back to the server-provided `user.effectiveRole` (computed at
// /auth/me time) when the licence context isn't yet available, so the
// very first paint after login is already correct.
export function useEffectiveRole() {
  const { user } = useAuth();
  const { snapshot } = useLicense();
  if (!user) return null;
  if (!snapshot) return user.effectiveRole ?? user.role;
  if (user.role === 'admin' && RESTRICTIVE_STATUSES.has(snapshot.status)) return 'user';
  return user.role;
}
